import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useFetcher } from '@remix-run/react';

import { PoweredByBeehiiv } from '~/components/PoweredByBeehiiv';
import { usePageContext } from '~/context/usePageContext';
import useEmail from '~/hooks/useEmail';
import usePublication from '~/hooks/usePublication';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { Recommendation } from '~/interfaces/recommendation';

import Continue from '../Index/components/Continue';
import Header from '../Index/components/Header';
import LoadMoreButton from '../Index/components/LoadMoreButton';
import PopupForm from '../Index/components/PopupForm';
import RecommendationItem from '../Index/components/RecommendationListItem';
import RecommendationsList from '../Index/components/RecommendationsList';

export default function RecommendationsForm({
  isLoading,
  recommendations,
  boostedRecommendations,
  hasNextPage,
  loadNextPage,
}: any) {
  const { t } = useTranslation();
  const publication = usePublication();
  const fetcher = useFetcher<{
    toast: { message: string; status: string };
    status: string;
  }>();
  const data = fetcher.data;
  const isSuccessful = data?.toast?.status === 'success';

  const [recommendation_ids, setSelections] = useState<string[]>([]);
  const [email] = useEmail();
  const [isOpen, setIsOpen] = useState(false);
  const { visitToken } = usePageContext();

  const { nextPage, progress } = useSignupRedirects(publication);

  useEffect(() => {
    if (isSuccessful) {
      nextPage();

      toast.success(data.toast.message);
    }
  }, [isSuccessful]);

  const onToggle = (id: Recommendation['id']) => {
    if (recommendation_ids.includes(id)) return setSelections(recommendation_ids.filter((s) => s !== id));

    setSelections([...recommendation_ids, id]);
  };

  const allSelectedRecommendations = [
    ...recommendations.filter((r: any) => recommendation_ids.includes(r.id)),
    ...boostedRecommendations.filter((r: any) => recommendation_ids.includes(r.id)),
  ];

  const needsDoubleOpt = allSelectedRecommendations.some((r) => r.double_opt_required);

  const singleSuccessMessage = needsDoubleOpt
    ? t('recommendations.toast.success_one_double_opt')
    : t('recommendations.toast.success_one');

  const multipleSuccessMessage = needsDoubleOpt
    ? t('recommendations.toast.success_other_double_opt', {
        count: recommendation_ids.length,
      })
    : t('recommendations.toast.success_other', {
        count: recommendation_ids.length,
      });

  const successMessage = recommendation_ids.length > 1 ? multipleSuccessMessage : singleSuccessMessage;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (email) {
      const formData = new FormData();
      formData.append('email', email);
      if (recommendation_ids.length > 0) {
        recommendation_ids.forEach((id) => {
          formData.append('recommendation_ids[]', id);
        });
      } else {
        const allRecommendationIds = recommendations
          .map((r: any) => r.id)
          .concat(boostedRecommendations.map((r: any) => r.id));

        allRecommendationIds.forEach((id: string) => {
          formData.append('recommendation_ids[]', id);
        });
      }
      formData.append('include_current_publication', 'false');
      formData.append('visit_token', visitToken || '');
      formData.append('publication_id', publication.id);
      const errorMessage = t('errors.default');
      formData.append('recommendations_error_message', errorMessage);
      formData.append('recommendations_success_message', successMessage);

      fetcher.submit(formData, {
        method: 'post',
        action: '/recommendations_subscribe_form',
      });
    } else {
      setIsOpen(true);
    }
  };

  const isNoneSelected = recommendation_ids.length === 0;
  const continueText = isNoneSelected ? t('recommendations.subscribe_to_all') : t('meta.titles.subscribe');

  return (
    <>
      <fetcher.Form className="relative mx-auto w-full max-w-3xl p-6" onSubmit={onSubmit}>
        <Header isSignupFlowInProgess={Boolean(progress)} publication={publication} />
        <RecommendationsList>
          {boostedRecommendations?.map((recommendation: Recommendation) => (
            <RecommendationItem
              boost
              selected={recommendation_ids.includes(recommendation.id)}
              onToggle={onToggle}
              recommendation={recommendation}
              key={recommendation.id}
            />
          ))}
          {recommendations.map((recommendation: any) => (
            <RecommendationItem
              selected={recommendation_ids.includes(recommendation.id)}
              onToggle={onToggle}
              recommendation={recommendation}
              key={recommendation.id}
            />
          ))}
        </RecommendationsList>
        {hasNextPage && <LoadMoreButton hasNextPage={hasNextPage} isLoading={isLoading} loadNextPage={loadNextPage} />}
        <Continue handleRedirect={nextPage}>{continueText}</Continue>

        <div className="flex items-center justify-center">
          <PoweredByBeehiiv publication={publication} />
        </div>
      </fetcher.Form>
      <PopupForm
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        recommendation_ids={recommendation_ids}
        successMessage={successMessage}
      />
    </>
  );
}
